import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { toast } from "sonner";
import { Upload, Paperclip, X, FileText, Image as ImageIcon, Truck } from "lucide-react";

const TYPE_LABEL = { return: "Return Request", cancellation: "Cancellation Request", inr: "Item Not Received" };

function StatusPill({ status }) {
  const closed = ["Refunded", "Closed - Resolved", "Closed - Denied"].includes(status);
  return (
    <span
      data-testid="case-status-pill"
      className={`inline-block font-mono text-[10px] uppercase tracking-editorial border px-2 py-1 ${
        closed ? "border-[#1A1714]/40 text-[#1A1714]/60" : "border-[#7A2021] text-[#7A2021]"
      }`}
    >
      {status}
    </span>
  );
}

function AttachmentPreview({ att }) {
  const isImg = (att.content_type || "").startsWith("image/");
  return (
    <a
      href={att.url}
      target="_blank"
      rel="noreferrer"
      className="flex items-center gap-2 px-2 py-1 border border-[#E5E1D8] hover:border-[#1A1714] bg-white transition-colors"
    >
      {isImg ? <ImageIcon size={13} /> : <FileText size={13} />}
      <span className="font-mono text-[10px] uppercase tracking-editorial truncate max-w-[180px]">{att.filename}</span>
    </a>
  );
}

function MessageBubble({ msg }) {
  const isBuyer = msg.author_type === "buyer";
  return (
    <div className={`flex ${isBuyer ? "justify-end" : "justify-start"}`} data-testid={`case-message-${msg.id}`}>
      <div className={`max-w-[80%] p-4 border ${isBuyer ? "border-[#1A1714] bg-white" : "border-[#7A2021] bg-[#F9F6F0]"}`}>
        <p className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60 mb-1">
          {isBuyer ? "You" : "Blessings Connection"} · {new Date(msg.created_at).toLocaleString()}
        </p>
        {msg.body && <p className="font-serif text-base text-[#1A1714] whitespace-pre-wrap break-words">{msg.body}</p>}
        {msg.attachments && msg.attachments.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2">
            {msg.attachments.map((a) => <AttachmentPreview key={a.path} att={a} />)}
          </div>
        )}
      </div>
    </div>
  );
}

export default function CaseDetail() {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [caseData, setCaseData] = useState(null);
  const [error, setError] = useState("");
  const [reply, setReply] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [loading, user, navigate]);

  const load = () => {
    if (!user) return;
    api.get(`/cases/${caseId}`)
      .then((r) => setCaseData(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Case not found"));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user, caseId]);

  const uploadOne = async (f) => {
    if (attachments.length >= 5) return toast.error("Max 5 attachments per message");
    if (f.size > 5 * 1024 * 1024) return toast.error(`${f.name} is over 5 MB`);
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/cases/upload-attachment", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setAttachments((a) => [...a, data]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const onFiles = async (e) => {
    for (const f of Array.from(e.target.files || [])) {
      // eslint-disable-next-line no-await-in-loop
      await uploadOne(f);
    }
    if (fileRef.current) fileRef.current.value = "";
  };

  const send = async (e) => {
    e.preventDefault();
    const body = reply.trim();
    if (!body && attachments.length === 0) return toast.error("Message or attachment required");
    setSending(true);
    try {
      const { data } = await api.post(`/cases/${caseId}/messages`, { body, attachments });
      setCaseData(data);
      setReply("");
      setAttachments([]);
      toast.success("Reply sent");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send reply");
    } finally {
      setSending(false);
    }
  };

  if (loading || (!caseData && !error)) {
    return (
      <div className="pt-40 max-w-3xl mx-auto px-6 md:px-10 pb-24">
        <p className="font-serif italic text-2xl text-[#1A1714]/60">Loading case…</p>
      </div>
    );
  }
  if (error) {
    return (
      <div className="pt-40 max-w-3xl mx-auto px-6 md:px-10 pb-24">
        <p className="font-serif text-2xl text-[#7A2021]" data-testid="case-error">{error}</p>
        <Link to="/account" className="mt-4 inline-block font-mono text-[11px] uppercase tracking-editorial underline">← Back to my account</Link>
      </div>
    );
  }

  const closed = !caseData.is_open;
  const totalRefunded = Number(caseData.total_refunded || 0);

  return (
    <div className="pt-28 md:pt-36 max-w-4xl mx-auto px-6 md:px-10 pb-24" data-testid="case-detail-page">
      <Link to="/account" className="font-mono text-[11px] uppercase tracking-editorial text-[#1A1714]/60 hover:text-[#7A2021]">← My account</Link>

      <div className="mt-6 flex flex-wrap items-baseline gap-4">
        <h1 className="font-serif text-5xl md:text-6xl tracking-tight leading-none">
          Case <span className="text-[#7A2021]">#{caseData.case_number}</span>
        </h1>
        <StatusPill status={caseData.status} />
      </div>
      <p className="font-mono text-[11px] uppercase tracking-editorial text-[#1A1714]/60 mt-3">
        {TYPE_LABEL[caseData.type]} · Order <Link to={`/track/${caseData.order_id}?email=${encodeURIComponent(user?.email || "")}`} className="underline hover:text-[#7A2021]">#{caseData.order_id}</Link> · Opened {new Date(caseData.created_at).toLocaleDateString()}
      </p>

      {/* Reason + refund summary */}
      <div className="mt-6 border border-[#E5E1D8] p-5 bg-white space-y-3">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">Reason</p>
          <p className="font-serif text-xl text-[#1A1714] mt-1" data-testid="case-reason">{caseData.reason}</p>
        </div>
        {totalRefunded > 0 && (
          <div>
            <p className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">Refunded</p>
            <p className="font-serif text-2xl text-[#7A2021]" data-testid="case-total-refunded">${totalRefunded.toFixed(2)}</p>
          </div>
        )}
      </div>

      {/* Thread */}
      <section className="mt-8 space-y-4" data-testid="case-thread">
        {(caseData.messages || []).map((m) => <MessageBubble key={m.id} msg={m} />)}
      </section>

      {/* Reply composer */}
      {!closed ? (
        <form onSubmit={send} className="mt-8 border border-[#E5E1D8] p-5 bg-white" data-testid="case-reply-form">
          <p className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60 mb-2">Send a reply</p>
          <Textarea
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            placeholder="Write a message…"
            className="rounded-none min-h-24 border-[#E5E1D8]"
            data-testid="case-reply-input"
          />
          {attachments.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {attachments.map((a, i) => (
                <div key={a.path} className="flex items-center gap-2 px-2 py-1 border border-[#E5E1D8] bg-[#F9F6F0]">
                  <Paperclip size={12} />
                  <span className="font-mono text-[10px] uppercase tracking-editorial truncate max-w-[160px]">{a.filename}</span>
                  <button type="button" onClick={() => setAttachments((arr) => arr.filter((_, j) => j !== i))} className="text-[#7A2021]"><X size={11} /></button>
                </div>
              ))}
            </div>
          )}
          <div className="mt-3 flex items-center gap-3 flex-wrap">
            <input ref={fileRef} type="file" multiple className="hidden" accept="image/jpeg,image/png,image/gif,image/webp,application/pdf" onChange={onFiles} data-testid="case-reply-file-input" />
            <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="case-reply-attach" className="rounded-none border-[#1A1714] font-mono text-[10px] uppercase tracking-editorial">
              <Upload size={12} className="mr-2" /> {uploading ? "Uploading…" : "Attach"}
            </Button>
            <Button type="submit" disabled={sending} data-testid="case-reply-send" className="rounded-none bg-[#1A1714] text-[#F9F6F0] hover:bg-[#7A2021] font-mono text-xs uppercase tracking-editorial">
              {sending ? "Sending…" : "Send reply"}
            </Button>
          </div>
        </form>
      ) : (
        <div className="mt-8 border border-[#E5E1D8] p-5 bg-[#F3EFE6]" data-testid="case-closed-banner">
          <p className="font-serif text-lg text-[#1A1714]">This case is closed.</p>
          <p className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60 mt-1">
            Status: {caseData.status}
          </p>
        </div>
      )}

      {caseData.tracking_number && (
        <Link
          to={`/track/${caseData.order_id}?email=${encodeURIComponent(user?.email || "")}`}
          className="mt-6 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60 hover:text-[#7A2021]"
        >
          <Truck size={12} /> View tracking for this order
        </Link>
      )}
    </div>
  );
}
